'use client'

import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, Search } from 'lucide-react'
import { useState } from 'react'
import { Link } from "react-router-dom"

const faqs = [
  {
    question: "How do I file my Income Tax Return (ITR) using TaxEases?",
    answer: "Filing your ITR with TaxEases is simple and straightforward. First, create an account or log in to your existing one. Then, upload your Form 16 and any other relevant documents. Our intelligent system will automatically fill in most details. Review the information, make any necessary adjustments, and submit your ITR. Our expert team will verify your submission before final filing with the Income Tax Department."
  },
  {
    question: "What documents do I need to file my taxes?",
    answer: "The main document you'll need is your Form 16, provided by your employer. You may also need bank statements, investment proofs (for tax-saving investments), rent receipts (if claiming HRA), and any other income proofs (like interest income or capital gains). If you're a freelancer or business owner, you'll need your income and expense statements. TaxEases provides a comprehensive checklist based on your specific situation."
  },
  {
    question: "How long does it take to process my tax return?",
    answer: "With TaxEases, most straightforward returns can be processed and filed within 24-48 hours of document submission. Complex returns might take 3-5 business days. After filing, the Income Tax Department typically processes returns in 20-45 days. You can track the status of your return in real-time through your TaxEases dashboard."
  },
  {
    question: "Can TaxEases help me maximize my tax deductions?",
    answer: "TaxEases uses advanced algorithms to analyze your financial data and identify all eligible deductions. Our system is updated with the latest tax laws and regulations. We'll guide you through potential deductions you might have missed, such as Section 80C investments, health insurance premiums, and more. Plus, our tax experts are available for personalized advice to optimize your tax savings."
  },
  {
    question: "What if I make a mistake in my tax filing?",
    answer: "Don't worry! If you notice a mistake after filing, TaxEases can help you file a revised return. If our team catches an error during the verification process, we'll contact you immediately to make corrections before filing. In case the IT Department raises any queries, our expert support team will assist you in responding and resolving the issue promptly."
  },
  {
    question: "Is my financial information secure with TaxEases?",
    answer: "Your security is our top priority. TaxEases uses bank-grade encryption to protect all your data. We comply with all data protection regulations and never share your information with third parties. Our systems undergo regular security audits, and we implement the latest cybersecurity measures to ensure your sensitive financial information remains confidential and secure."
  }
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
}

const contentVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: {
    height: "auto",
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
}

export default function TaxServiceFAQ() {
  const [openIndex, setOpenIndex] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-cyan-100 py-16 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto" // Increased max-width
      >
        <motion.h2 
          variants={itemVariants}
          className="text-4xl md:text-5xl font-bold text-cyan-800 text-center mb-8"
        >
          Frequently Asked Questions
        </motion.h2>
        
        <motion.p
          variants={itemVariants}
          className="text-xl text-cyan-600 text-center mb-12 max-w-3xl mx-auto"
        >
          Get quick answers to common questions about TaxEases and our tax filing services.
        </motion.p>

        <motion.div variants={itemVariants} className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 rounded-full border-2 border-cyan-300 focus:border-cyan-500 focus:ring focus:ring-cyan-200 focus:ring-opacity-50 transition duration-300 text-cyan-800 placeholder-cyan-400"
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-cyan-400" />
          </div>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          className="space-y-6"
        >
          {filteredFaqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className={`bg-white rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index 
                  ? 'shadow-lg ring-2 ring-cyan-500 ring-opacity-50' 
                  : 'shadow-md hover:shadow-lg'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                className="w-full flex items-center justify-between p-4 text-left transition-colors duration-200"
              >
                <span className={`font-semibold text-xxl ${
                  openIndex === index ? 'text-cyan-600' : 'text-cyan-800'
                }`}>
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex-shrink-0 ml-4 ${
                    openIndex === index ? 'text-cyan-500' : 'text-cyan-400'
                  }`}
                >
                  <ChevronDown className="w-8 h-8" />
                </motion.div>
              </button>
              
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="overflow-hidden bg-cyan-50"
                  >
                    <div className="p-6 text-cyan-700 leading-relaxed text-xxl">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {filteredFaqs.length === 0 && (
          <motion.p
            variants={itemVariants}
            className="text-center text-cyan-600 mt-8"
          >
            No matching questions found. Please try a different search term.
          </motion.p>
        )}

        <motion.div
          variants={itemVariants}
          className="mt-12 text-center"
        >
          <p className="text-cyan-700 mb-4">Still have questions?</p>
          <Link
            to="#contact"
            className="inline-block bg-cyan-600 text-white font-semibold py-3 px-8 rounded-full hover:bg-cyan-700 transition duration-300 shadow-md hover:shadow-lg"
          >
            Contact Our Support Team
          </Link>
        </motion.div>
      </motion.div>
    </div>
  )
}

